// Copy / paste / duplicate of a canvas element, as pure functions over the
// editor's formData. ONE implementation: the keyboard shortcuts (⌘C / ⌘V /
// ⌘D) and the Layers panel buttons both call these, so they cannot drift.
//
// Before this the logic lived inline in three keydown branches, and the
// duplicate branch read a constant declared inside the paste branch -- ⌘D
// threw a ReferenceError and nothing was duplicated. A pasted text field also
// moved its center without its bounds, orphaning center_x outside the box
// (the #4575 shape: production anchors at center_x with no clamp).

// Fraction of the canvas a pasted copy is shifted by, so it is visibly a
// second element rather than a perfect overlap of the original.
export const PASTE_OFFSET = 0.03;

const clamp01 = (v) => Math.max(0, Math.min(1, v));

const holdsPhotos = (formData) => formData.look_type === 'collage' || formData.look_type === 'gif_slide';

// What the clipboard holds for the selected element, or null if there is
// nothing copyable selected. A text field loses its id (the copy is a new
// row); a photo is copied whole (look_ref / upload source, geometry, tilt).
export const copyElement = (formData, selected) => {
  if (!selected) return null;

  if (selected.type === 'text' && selected.index != null) {
    const tf = (formData.text_fields || [])[selected.index];
    return tf ? { type: 'text', data: { ...tf, id: undefined } } : null;
  }
  if (selected.type === 'collage_photo' && selected.index != null) {
    const photo = (formData.collage_photos || [])[selected.index];
    return photo ? { type: 'collage_photo', data: { ...photo } } : null;
  }
  if (selected.type === 'face') {
    return {
      type: 'face',
      data: {
        eyes_y: formData.eyes_y,
        face_height: formData.face_height,
        center_x: formData.center_x,
        rotation: formData.rotation,
      },
    };
  }
  if (selected.type === 'mascot') {
    return {
      type: 'mascot',
      data: {
        mascot_center_x: formData.mascot_center_x,
        mascot_center_y: formData.mascot_center_y,
        mascot_size: formData.mascot_size,
      },
    };
  }
  return null;
};

// The formData with the clip pasted in, and the element to select next
// (the new copy), or null when the clip cannot land on this look.
export const pasteElement = (formData, clip) => {
  if (!clip) return null;

  if (clip.type === 'text') {
    const fields = formData.text_fields || [];
    // The whole box moves, not just its center: bounds travel with center_x
    // so the copy is as well-authored as the original.
    const shift = Math.min(PASTE_OFFSET, 1 - (clip.data.bound_x_right ?? 1));
    const newField = {
      ...clip.data,
      id: undefined,
      position: fields.length,
      center_x: clamp01((clip.data.center_x ?? 0.5) + shift),
      center_y: clamp01((clip.data.center_y ?? 0.5) + PASTE_OFFSET),
      bound_x_left: clamp01((clip.data.bound_x_left ?? 0) + shift),
      bound_x_right: clamp01((clip.data.bound_x_right ?? 1) + shift),
    };
    return {
      formData: { ...formData, text_fields: [...fields, newField] },
      selected: { type: 'text', index: fields.length },
    };
  }

  if (clip.type === 'collage_photo') {
    if (!holdsPhotos(formData)) return null;
    const photos = formData.collage_photos || [];
    const newPhoto = {
      ...clip.data,
      x: clamp01((clip.data.x ?? 0) + PASTE_OFFSET),
      y: clamp01((clip.data.y ?? 0) + PASTE_OFFSET),
    };
    return {
      formData: { ...formData, collage_photos: [...photos, newPhoto] },
      selected: { type: 'collage_photo', index: photos.length },
    };
  }

  // Face and mascot are singletons: pasting moves the one there is.
  if (clip.type === 'face') {
    return {
      formData: {
        ...formData,
        eyes_y: clamp01((clip.data.eyes_y ?? 0.3) + PASTE_OFFSET),
        face_height: clip.data.face_height,
        center_x: clamp01((clip.data.center_x ?? 0.5) + PASTE_OFFSET),
        rotation: clip.data.rotation,
      },
      selected: { type: 'face' },
    };
  }
  if (clip.type === 'mascot') {
    return {
      formData: {
        ...formData,
        mascot_center_x: clamp01((clip.data.mascot_center_x ?? 0.5) + PASTE_OFFSET),
        mascot_center_y: clamp01((clip.data.mascot_center_y ?? 0.5) + PASTE_OFFSET),
        mascot_size: clip.data.mascot_size,
      },
      selected: { type: 'mascot' },
    };
  }
  return null;
};

// ⌘D: copy and paste in one step. Only text fields and photos can exist
// twice; a face or mascot "duplicate" would just nudge the original.
export const duplicateElement = (formData, selected) => {
  if (!selected || (selected.type !== 'text' && selected.type !== 'collage_photo')) return null;
  return pasteElement(formData, copyElement(formData, selected));
};

export const canDuplicate = (selected) => !!selected
  && (selected.type === 'text' || selected.type === 'collage_photo');

// The selection after element `index` of `type` is removed. Selection is
// keyed by array index, so a removal below the selected element shifts it
// down by one; removing the selected element itself clears it. Without this
// the Text tab outline, the Layers highlight and ⌘C / ⌘D all act on whatever
// slid into the old index.
export const selectionAfterRemoval = (selected, type, index) => {
  if (!selected || selected.type !== type || selected.index == null) return selected;
  if (selected.index === index) return null;
  return selected.index > index ? { ...selected, index: selected.index - 1 } : selected;
};

// Which index a single removal took out of `prev` to produce `next`, or null
// if `next` is not `prev` minus one element. Array#filter keeps element
// identity, so the removed index is the first position where the two
// disagree. Lets the editor re-key its selection on removal paths that only
// hand back the shorter array (the Look tab's photo list).
export const removedIndexOf = (prev, next) => {
  if (!Array.isArray(prev) || !Array.isArray(next) || next.length !== prev.length - 1) return null;
  const i = prev.findIndex((el, idx) => el !== next[idx]);
  return i === -1 ? prev.length - 1 : i;
};
